import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  getConfigurations,
  addConfiguration,
  updateConfiguration,
  deleteConfiguration,
  searchConfigurations,
  selectConfiguration,
  clearError,
  getCategories,
} from '../../actions/aiAssistConfigActions';
import SavedConfigurationModal from './savedConfigurationModal.jsx';
import Loading from '../common/Loading';
import './index.scss';
import Select, { components } from 'react-select';

const DropdownIndicator = (props) =>
  components.DropdownIndicator && (
    <components.DropdownIndicator {...props}>
      <i className="material-icons custom">arrow_drop_down</i>
    </components.DropdownIndicator>
  );

const multiSelectdropdownCustomStyle = {
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#007bff' : 'white',
    padding: '10px',
    color: '#000',
    ':hover': {
      backgroundColor: '#f0f0f0',
    },
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 200,
    overflowY: 'auto',
  }),
  control: (base) => ({
    ...base,
    border: '1px solid #CFD0D1',
    padding: '6px',
    boxShadow: 'none',
    outline: 'none',
    '&:hover': {
      border: '1px solid #CFD0D1',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0px',
  }),
};

class AIAssists extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      contextInputValue: '',
      responseInputValue: '',
      selectedCategory: [],
      expiryDate: this.getTomorrowDate(),
      showModal: false,
      searchTerm: '',
      editMode: false,
      editingId: null,
      audioEnabled: false,
      bulkOperationInProgress: false,
      bulkOperationStatus: null,
      bulkOperationError: null,
      availableCategories: [],
      loading: false,
      modalDataLoaded: false,
      modalLoading: false,
      saveLoading: false,
      categoriesLoading: false,
      bzIds: [this.getBusinessId()],
      showConfirmModal: false,
      confirmModalConfig: {
        title: '',
        message: '',
        onConfirm: null,
        onCancel: null,
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        type: 'confirm'
      },
      showSuccessModal: false,
      successMessage: '',
      showErrorModal: false,
      errorMessage: ''
    };
  }

  validateField = (fieldValue, fieldName) => {
    const errors = [];
    
    if (typeof fieldValue === "undefined") {
      errors.push(`${fieldName} cannot be undefined.`);
      return errors;
    }
    
    if (fieldValue === null) {
      errors.push(`${fieldName} cannot be null.`);
      return errors;
    }
    
    if (typeof fieldValue !== "string") {
      errors.push(`${fieldName} must be a string, not ${typeof fieldValue}.`);
      return errors;
    }
    
    if (fieldValue === '') {
      errors.push(`${fieldName} cannot be an empty string.`);
      return errors;
    }
    
    if (fieldValue.trim() === '') {
      errors.push(`${fieldName} cannot contain only whitespace.`);
      return errors;
    }
    
    const emojiRegex = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1F018}-\u{1F270}]|[\u{238C}-\u{2454}]|[\u{20D0}-\u{20FF}]|[\u{FE00}-\u{FE0F}]|[\u{1F004}]|[\u{1F0CF}]|[\u{1F170}-\u{1F251}]/gu;
    
    if (emojiRegex.test(fieldValue)) {
      errors.push(`${fieldName} cannot contain emojis.`);
    }
    
    const onlySpecialCharsRegex = /^[^a-zA-Z0-9\s]+$/;
    if (onlySpecialCharsRegex.test(fieldValue.trim())) {
      errors.push(`${fieldName} cannot contain only special characters.`);
    }

    const onlyNumbersRegex = /^\d+$/;
    if (onlyNumbersRegex.test(fieldValue.trim())) {
      errors.push(`${fieldName} cannot contain only numbers.`);
    }

    const noLettersRegex = /^[^a-zA-Z]+$/;
    if (noLettersRegex.test(fieldValue.trim())) {
      errors.push(`${fieldName} must contain at least one letter.`);
    }

    return errors;
  };

  validateExpiryDate = (dateValue) => {
    const errors = [];
    
    if (!dateValue || dateValue.trim() === '') {
      errors.push('Expiry date is required');
      return errors;
    }

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateValue)) {
      errors.push('Expiry date must be in YYYY-MM-DD format');
      return errors;
    }

    const expiryDate = new Date(dateValue);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (isNaN(expiryDate.getTime())) {
      errors.push('Invalid expiry date');
      return errors;
    }

    if (expiryDate <= today) {
      errors.push('Expiry date must be a future date');
      return errors;
    }

    return errors;
  };

  getApiHeaders = () => {
    return {
      'Content-Type': 'application/json',
      'Cookie': 'JSESSIONID=node0mt398ixb5eve1kcbomgrdawu00.node0; pac4jCsrfToken=41bf45c5-8ce2-43a6-9532-789fb41d7a40'
    };
  };

  parseApiResponse = async (response) => {
    try {
      const contentType = response.headers.get('content-type');
      const responseText = await response.text();
      if (contentType && contentType.includes('application/json')) {
        try {
          const result = JSON.parse(responseText);
          return result;
        } catch (jsonError) {
          return responseText;
        }
      } else {
        return responseText;
      }
    } catch (error) {
      throw new Error(`Failed to read API response: ${error.message}`);
    }
  };

  addConfigurationViaAPI = async (configData, bzIds = null) => {
    try {
      // Format the configuration data to match what the action expects
      const formattedConfig = {
        UserMessage: configData.UserMessage || configData.context,
        Response: configData.Response || configData.response,
        Categories: Array.isArray(configData.Categories) ? configData.Categories : [configData.Categories || "General FAQ"],
        ExpiryDate: configData.expiryDate || this.getTomorrowDate()
      };
      const result = await this.props.addConfiguration(formattedConfig, bzIds);
      return result;
  
    } catch (error) {
      console.error('Error in addConfigurationViaAPI:', error);
      throw error;
    }
  };

  showConfirmDialog = (title, message, onConfirm, confirmText = 'Confirm', cancelText = 'Cancel') => {
    this.setState({
      showConfirmModal: true,
      confirmModalConfig: {
        title,
        message,
        onConfirm,
        onCancel: () => this.setState({ showConfirmModal: false }),
        confirmText,
        cancelText,
        type: 'confirm'
      }
    });
  };

  showSuccessDialog = (message) => {
    this.setState({
      showSuccessModal: false,
      successMessage: '',
      showErrorModal: false,
      errorMessage: '',
      showConfirmModal: false,
    }, () => {
      this.setState({
        showSuccessModal: true,
        successMessage: message,
      })
    });
  };

  showErrorDialog = (message) => {
    this.setState({
      showErrorModal: true,
      errorMessage: message
    });
  };

  closeAllModals = () => {
    this.setState({
      showConfirmModal: false,
      showSuccessModal: false,
      showErrorModal: false,
      confirmModalConfig: {
        title: '',
        message: '',
        onConfirm: null,
        onCancel: null,
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        type: 'confirm'
      },
      successMessage: '',
      errorMessage: ''
    });
  };

  async componentDidMount() {
    try {
      this.setState({ categoriesLoading: true });
      const categories = await this.props.getCategories();
      if (Array.isArray(categories)) {
        this.setState({ availableCategories: categories });
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      this.showErrorDialog('Failed to load. Please refresh the page and try again.');
    } finally {
      this.setState({ categoriesLoading: false });
    }
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.currentConfiguration !== this.props.currentConfiguration &&
      this.props.currentConfiguration !== null
    ) {
      const config = this.props.currentConfiguration;
      const userMessage = config.UserMessage || config.Context || '';
      const response = config.Response || '';

      let expiryDate = config.expiryDate || this.getTomorrowDate();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const configExpiryDate = new Date(expiryDate);
      
      if (configExpiryDate <= today) {
        expiryDate = this.getTomorrowDate();
      }
      
      const categories = Array.isArray(config.Categories)
        ? config.Categories
        : [];

      const selectedCategory =
        categories.length > 0
          ? { value: categories[0], label: categories[0] }
          : null;

      this.setState({
        contextInputValue: userMessage,
        responseInputValue: response,
        selectedCategory: selectedCategory,
        expiryDate: expiryDate,
        editMode: true,
        editingId: config.id,
      });
    }

    if (
      prevProps.configurations !== this.props.configurations &&
      this.props.configurations &&
      this.props.configurations.length > 0 &&
      this.state.showModal
    ) {
      this.setState({ modalDataLoaded: true });
    }
  }

  getBusinessId = () => {
    return this.props.auth.studio_id
  }

  getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  handleContextChange = (e) => {
    this.setState({ contextInputValue: e.target.value });
  };

  handleResponseChange = (e) => {
    this.setState({ responseInputValue: e.target.value });
  };

  handleCategoryChange = (selectedOption) => {
    this.setState({ selectedCategory: selectedOption });
  };

  handleInputChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handleSearch = (e) => {
    const searchTerm = e.target.value;
    this.setState({ searchTerm });
    this.props.searchConfigurations(searchTerm);
  };

  handleSaveConfig = async (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
      e.stopPropagation();
    }
  
    const {
      responseInputValue,
      contextInputValue,
      selectedCategory,
      expiryDate,
      editMode,
      editingId
    } = this.state;

    const validationErrors = [];
    
    const contextErrors = this.validateField(contextInputValue, 'Context');
    validationErrors.push(...contextErrors);
    
    const responseErrors = this.validateField(responseInputValue, 'Response');
    validationErrors.push(...responseErrors);

    const expiryDateErrors = this.validateExpiryDate(expiryDate);
    validationErrors.push(...expiryDateErrors);
  
    if (!selectedCategory || 
        (Array.isArray(selectedCategory) && selectedCategory.length === 0) ||
        (!Array.isArray(selectedCategory) && !selectedCategory.value)) {
      validationErrors.push('Category is required. Please select at least one category.');
    }
  
    if (validationErrors.length > 0) {
      this.showErrorDialog(validationErrors.join('\n'));
      return;
    }
  
    try {
      this.setState({ saveLoading: true });
  
      if (editMode && editingId) {
        const originalConfig = this.props.configurations?.find(c => c.id === editingId);
  
        if (!originalConfig) {
          this.showErrorDialog('Original configuration not found.');
          this.setState({ saveLoading: false });
          return;
        }
  
        const updatedFields = {};
  
        if (contextInputValue.trim() !== originalConfig.UserMessage) {
          updatedFields.UserMessage = contextInputValue.trim();
        }
  
        if (responseInputValue.trim() !== originalConfig.Response) {
          updatedFields.Response = responseInputValue.trim();
        }
  
        let selectedCategories = [];
        if (Array.isArray(selectedCategory)) {
          selectedCategories = selectedCategory.map(cat => cat.value);
        } else if (selectedCategory && selectedCategory.value) {
          selectedCategories = [selectedCategory.value];
        } else {
          selectedCategories = ["General FAQ"];
        }
  
        const originalCategories = originalConfig.Categories || ["General FAQ"];
        
        const categoriesChanged = selectedCategories.length !== originalCategories.length ||
          selectedCategories.some((cat, index) => cat !== originalCategories[index]);
  
        if (categoriesChanged) {
          updatedFields.Categories = selectedCategories;
        }
  
        const selectedExpiryDate = expiryDate?.toString() || this.getTomorrowDate().toString();
        if (selectedExpiryDate !== originalConfig.expiryDate) {
          updatedFields.expiryDate = selectedExpiryDate;
        }
  
        if (Object.keys(updatedFields).length === 0) {
          this.showSuccessDialog("No changes detected.");
          this.setState({ saveLoading: false });
          return;
        }
  
        updatedFields.id = editingId;
        updatedFields._originalId = originalConfig._originalId;
        updatedFields._documentId = originalConfig._documentId;
        updatedFields._newSolrId = originalConfig._newSolrId;
  
        await this.props.updateConfiguration(updatedFields);
        this.showSuccessDialog('Configuration updated successfully!');
      } else {
        let categories = [];
        if (Array.isArray(selectedCategory)) {
          categories = selectedCategory.map(cat => cat.value);
        } else if (selectedCategory && selectedCategory.value) {
          categories = [selectedCategory.value];
        } else {
          categories = ["General FAQ"];
        }
  
        const newConfig = {
          UserMessage: contextInputValue.trim(),
          Response: responseInputValue.trim(),
          expiryDate: expiryDate.toString(),
          Categories: categories
        };
  
        await this.addConfigurationViaAPI(newConfig);
        this.showSuccessDialog('Configuration saved successfully!');
      }
  
      this.resetForm();
      await this.props.getConfigurations();
  
    } catch (error) {
      console.error('Error saving configuration:', error);
      this.showErrorDialog(`Failed to save configuration: ${error?.message || 'Unknown error'}`);
    } finally {
      this.setState({ saveLoading: false });
    }
  };

  resetForm = () => {
    this.setState({
      responseInputValue: '',
      contextInputValue: '',
      selectedCategory: [],
      expiryDate: this.getTomorrowDate(),
      editMode: false,
      editingId: null,
    });
    this.props.selectConfiguration({ configuration: null });
  };

  handleCancel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.resetForm();
  };

  toggleModal = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!this.state.showModal) {
      try {
        this.setState({ modalLoading: true, modalDataLoaded: false });
        const { aiAssistConfigReducer} = this.props;
        if (Array.isArray(aiAssistConfigReducer?.categories)) {
          this.setState({ availableCategories: aiAssistConfigReducer?.categories });
        }
        
        this.setState({ 
          modalLoading: false,
          modalDataLoaded: true,
          showModal: true
        });
      } catch (error) {
        console.error('Error fetching data for modal:', error);
        this.setState({ 
          modalLoading: false,
          bulkOperationError: `Failed to load data: ${error.message || 'Unknown error'}`,
          showModal: true
        });
      }
    } else {
      this.setState({ showModal: false });
    }
  };

  toggleAudioEnabled = () => {
    this.setState((prevState) => ({
      audioEnabled: !prevState.audioEnabled,
    }));
  };

  handleCreateNewConfig = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.resetForm();
  };

  handleAddConfiguration = async (configData) => {
    try {
      const contextValue = configData.UserMessage || configData.user_message || configData.context;
      const responseValue = configData.Response || configData.response;
      
      const validationErrors = [];
      
      const contextErrors = this.validateField(contextValue, 'Context');
      validationErrors.push(...contextErrors);
      
      const responseErrors = this.validateField(responseValue, 'Response');
      validationErrors.push(...responseErrors);
      
      let expiryDate = configData.ExpiryDate || configData.expiryDate || this.getTomorrowDate();
      const expiryDateErrors = this.validateExpiryDate(expiryDate);
      validationErrors.push(...expiryDateErrors);
      
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join('\n'));
      }

      const categories = Array.isArray(configData.Categories)
        ? configData.Categories
        : Array.isArray(configData.category)
        ? configData.category
        : configData.Categories
        ? [configData.Categories]
        : configData.category
        ? [configData.category]
        : ["General FAQ"];

      if (categories.length === 0 || !categories[0]) {
        throw new Error('At least one category is required');
      }

      const formattedConfig = {
        UserMessage: contextValue.trim(),
        Response: responseValue.trim(),
        expiryDate: expiryDate.toString(),
        Categories: categories
      };    

      await this.addConfigurationViaAPI(formattedConfig);
      await this.props.getConfigurations();

      return true;
    } catch (error) {
      console.error('Error adding configuration:', error);
      throw new Error(
        `Failed to add configuration: ${error.message || 'Unknown error'}`,
      );
    }
  };

  handleBulkAddConfigurations = async (configurations, bzIds = null) => {
    if (!Array.isArray(configurations) || configurations.length === 0) {
      throw new Error('No configurations provided for bulk add');
    }
  
    this.setState({
      bulkOperationInProgress: true,
      bulkOperationStatus: `Preparing to add ${configurations.length} configurations...`,
      bulkOperationError: null,
    });
  
    try {
      const formattedConfigurations = configurations.map(config => ({
        UserMessage: config.user_message || config.UserMessage || config.context || '',
        Response: config.response || config.Response || '',
        Categories: Array.isArray(config.category) 
          ? config.category : Array.isArray(config.Categories)
          ? config.Categories : config.category 
          ? [config.category] : config.Categories 
          ? [config.Categories] : ["General FAQ"],
        ExpiryDate: config.expiryDate || config.ExpiryDate || this.getTomorrowDate()
      }));
  
      const validationErrors = [];
      formattedConfigurations.forEach((config, index) => {
        const contextErrors = this.validateField(config.UserMessage, `Configuration ${index + 1} Context`);
        validationErrors.push(...contextErrors);
        
        const responseErrors = this.validateField(config.Response, `Configuration ${index + 1} Response`);
        validationErrors.push(...responseErrors);
        
        const expiryDateErrors = this.validateExpiryDate(config.ExpiryDate);
        validationErrors.push(...expiryDateErrors.map(err => `Configuration ${index + 1}: ${err}`));
        
        if (!config.Categories || config.Categories.length === 0) {
          validationErrors.push(`Configuration ${index + 1}: Category is required`);
        }
      });
  
      if (validationErrors.length > 0) {
        throw new Error('Validation failed:\n' + validationErrors.join('\n'));
      }
  
      this.setState({
        bulkOperationStatus: `Adding ${configurations.length} configurations...`,
      });
  
      const result = await this.props.addConfiguration(formattedConfigurations);
  
      this.setState({
        bulkOperationStatus: 'Refreshing configuration list...',
      });
  
      await this.props.getConfigurations();
  
      this.setState({
        bulkOperationInProgress: false,
        bulkOperationStatus: null,
        bulkOperationError: null,
      });
  
      const message = `Bulk operation completed successfully. Added ${configurations.length} configurations.`;
      this.showSuccessDialog(message);
  
      return true;
    } catch (error) {
      console.error('Error in bulk add operation via Redux action:', error);
      this.setState({
        bulkOperationInProgress: false,
        bulkOperationStatus: null,
        bulkOperationError: `Bulk add operation failed: ${error.message}`,
      });
      throw error;
    }
  };

  render() {
    const {
      responseInputValue,
      contextInputValue,
      selectedCategory,
      expiryDate,
      showModal,
      searchTerm,
      editMode,
      audioEnabled,
      bulkOperationInProgress,
      bulkOperationStatus,
      bulkOperationError,
      availableCategories,
      modalDataLoaded,
      modalLoading,
      saveLoading,
      categoriesLoading,
      showConfirmModal,
      confirmModalConfig,
      showSuccessModal,
      successMessage,
      showErrorModal,
      errorMessage
    } = this.state;
  
    const {
      configurations,
      aiAssistConfigReducer,
      filteredConfigurations,
      loading: reduxLoading,
      error,
      auth
    } = this.props;
  
    const loading = reduxLoading;
    const displayedConfigurations =
      filteredConfigurations && filteredConfigurations.length > 0
        ? filteredConfigurations
        : configurations;
  
    const categoryOptions = availableCategories.map((category) => ({
      value: category,
      label: category,
    }));

    if (modalLoading) {
      return (
        <div className="ai-assist-container">
          <div className="ai-assist">
            <div className="ai-assist-header">
              <div className="title">
                <span>AI Assist Config</span>
              </div>
            </div>
            <div className="ai-assist-overview">
              <main className="ai-assist-main">
                <div className="full-page-loader">
                  <Loading />
                  <p>Loading AI Assists data...</p>
                </div>
              </main>
            </div>
          </div>
        </div>
      );
    }

    if (categoriesLoading && availableCategories.length === 0) {
      return (
        <div className="ai-assist-container">
          <div className="ai-assist">
            <div className="ai-assist-header">
              <div className="title">
                <span>AI Assist Config</span>
              </div>
            </div>
            <div className="ai-assist-overview">
              <main className="ai-assist-main">
                <div className="full-page-loader">
                  <Loading />
                  <p>Loading AI Assist Config...</p>
                </div>
              </main>
            </div>
          </div>
        </div>
      );
    }

    if (saveLoading) {
      return (
        <div className="ai-assist-container">
          <div className="ai-assist">
            <div className="ai-assist-header">
              <div className="title">
                <span>AI Assist Config</span>
                {editMode && <span className="edit-indicator"> (Editing)</span>}
              </div>
            </div>
            <div className="ai-assist-overview">
              <main className="ai-assist-main">
                <div className="full-page-loader">
                  <Loading />
                  <p>{editMode ? 'Updating Configuration...' : 'Saving Configuration...'}</p>
                </div>
              </main>
            </div>
          </div>
        </div>
      );
    }

    if (bulkOperationInProgress) {
      return (
        <div className="ai-assist-container">
          <div className="ai-assist">
            <div className="ai-assist-header">
              <div className="title">
                <span>AI Assist Config</span>
              </div>
            </div>
            <div className="ai-assist-overview">
              <main className="ai-assist-main">
                <div className="full-page-loader">
                  <Loading />
                  <p>{bulkOperationStatus || 'Processing bulk operation...'}</p>
                </div>
              </main>
            </div>
          </div>
        </div>
      );
    }

    console.log('availableCategories', this.state, this.state.availableCategories);
    return (
      <div className="ai-assist-container">
        <div className="ai-assist">
          <div className="ai-assist-header">
            <div className="title">
              <span>AI Assist Config</span>
              {editMode && <span className="edit-indicator"> (Editing)</span>}
            </div>
          </div>
          <div className="ai-assist-overview">
            <main className="ai-assist-main">
              <div className="ai-assist-header-buttons">
                <button
                  type="button"
                  className="ai-assist-button ai-assist-button--primary btn btn--primary btn--lg"
                  onClick={this.toggleModal}
                  disabled={modalLoading}
                >
                  AI Assists
                </button>
                {editMode && (
                  <button
                    type="button"
                    className="ai-assist-button ai-assist-button--secondary"
                    onClick={this.handleCreateNewConfig}
                    disabled={modalLoading}
                  >
                    Create New
                  </button>
                )}
              </div>
  
              <div className="ai-assist-panel">
                <div className="ai-assist-context-input">
                  <h2 className="ai-assist-context-input__title">Context</h2>
                  <div className="ai-assist-context-input__preview">
                    <textarea
                      value={contextInputValue}
                      onChange={this.handleContextChange}
                      className="ai-assist-context-input__field"
                      placeholder="Enter the context"
                      rows="4"
                      required
                      disabled={modalLoading}
                    />
                  </div>
                </div>
  
                <div className="ai-assist-panel__response">
                  <h2 className="ai-assist-panel__title">Response</h2>
  
                  <div className="ai-assist-response">
                    <textarea
                      value={responseInputValue}
                      onChange={this.handleResponseChange}
                      className="ai-assist-response__textarea"
                      placeholder="Enter the response"
                      rows="4"
                      required
                      disabled={modalLoading}
                    />
                  </div>
  
                  <div className="ai-assist-input-group">
                    <h3 className="ai-assist-input-group__title">
                      Expiry Date:
                    </h3>
                    <input
                      type="date"
                      id="expiryDate"
                      name="expiryDate"
                      value={expiryDate}
                      min={this.getTomorrowDate()}
                      onChange={this.handleInputChange}
                      className="ai-assist-input-group__date"
                      disabled={modalLoading}
                      onKeyDown={(e) => e.preventDefault()}
                      onPaste={(e) => e.preventDefault()}
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
  
                  <div className="ai-assist-category-input">
                    <h2 className="ai-assist-panel__title">Category</h2>
                    <div className="ai-assist-category-input__container">
                      <Select
                        isMulti
                        options={categoryOptions}
                        value={selectedCategory}
                        onChange={this.handleCategoryChange}
                        className="ai-assist-category-input__field"
                        components={{
                          DropdownIndicator,
                          IndicatorSeparator: () => null,
                        }}
                        styles={multiSelectdropdownCustomStyle}
                        placeholder="Select a category"
                        isDisabled={modalLoading}
                        isClearable={true}
                        isLoading={categoriesLoading}
                      />
                    </div>
                  </div>
  
                  <div className="ai-assist-actions">
                    {editMode && (
                      <button
                        type="button"
                        onClick={this.handleCancel}
                        className="ai-assist-button ai-assist-button--secondary"
                        disabled={modalLoading}
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={this.handleSaveConfig}
                      className="ai-assist-button ai-assist-button--primary btn btn--primary btn--lg"
                      disabled={modalLoading}
                    >
                      {editMode ? 'Update' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
  
        {showModal && !modalLoading && (
          <SavedConfigurationModal
            onClose={this.toggleModal}
            searchTerm={searchTerm}
            onSearch={this.handleSearch}
            configurations={displayedConfigurations || []}
            onEdit={(config) =>
              this.props.selectConfiguration({ configuration: config })
            }
            onUpdate={this.props.updateConfiguration}
            onDelete={(configToDelete) => this.props.deleteConfiguration(configToDelete)}
            onAdd={this.handleAddConfiguration}
            onBulkAdd={this.handleBulkAddConfigurations}
            loading={loading && !modalDataLoaded}
            bulkOperationInProgress={false}
            availableCategories={aiAssistConfigReducer?.categories || this.state.availableCategories}
            getConfigurations={this.props.getConfigurations}
            getCategories={this.props.getCategories}
            bulkAddConfigurations={this.handleBulkAddConfigurations}
          />
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="modal-overlay" onClick={confirmModalConfig.onCancel}>
            <div className="modal-content confirmation-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{confirmModalConfig.title}</h2>
                <button className="close-btn" onClick={confirmModalConfig.onCancel}>
                  <span className="material-icons">close</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="confirmation-content">
                  <span className="material-icons confirmation-icon">help_outline</span>
                  <p>{confirmModalConfig.message}</p>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  className="cancel-btn" 
                  onClick={confirmModalConfig.onCancel}
                >
                  {confirmModalConfig.cancelText}
                </button>
                <button 
                  className="confirm-btn" 
                  onClick={confirmModalConfig.onConfirm}
                >
                  {confirmModalConfig.confirmText}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Modal */}
        {showSuccessModal && (
          <div className="modal-overlay" onClick={this.closeAllModals}>
            <div className="modal-content success-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Success</h2>
                <button className="close-btn" onClick={this.closeAllModals}>
                  <span className="material-icons">close</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="success-content">
                  <span className="material-icons success-icon">check_circle</span>
                  <p>{successMessage}</p>
                </div>
              </div>
              <div className="modal-footer">
                <button className="ok-btn" onClick={this.closeAllModals}>
                  OK
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Modal */}
        {showErrorModal && (
          <div className="modal-overlay" onClick={this.closeAllModals}>
            <div className="modal-content error-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Error</h2>
                <button className="close-btn" onClick={this.closeAllModals}>
                  <span className="material-icons">close</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="error-content">
                  <span className="material-icons error-icon">error</span>
                  <p>{errorMessage}</p>
                </div>
              </div>
              <div className="modal-footer">
                <button className="ok-btn" onClick={this.closeAllModals}>
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
  
        {error && (
          <div className="error-notification">
            <span className="material-icons">error</span>
            <span>{error}</span>
            <button onClick={() => this.props.clearError()}>
              <span className="material-icons">close</span>
            </button>
          </div>
        )}
      </div>
    );
  }
}

AIAssists.propTypes = {
  getConfigurations: PropTypes.func.isRequired,
  addConfiguration: PropTypes.func.isRequired,
  updateConfiguration: PropTypes.func.isRequired,
  deleteConfiguration: PropTypes.func.isRequired,
  searchConfigurations: PropTypes.func.isRequired,
  selectConfiguration: PropTypes.func.isRequired,
  clearError: PropTypes.func.isRequired,
  getCategories: PropTypes.func.isRequired,
  configurations: PropTypes.array,
  filteredConfigurations: PropTypes.array,
  currentConfiguration: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

const mapStateToProps = (state) => ({
  configurations: state.configurations,
  filteredConfigurations: state.filteredConfigurations,
  currentConfiguration: state.currentConfiguration,
  loading: state.loading,
  error: state.error,
});

export default connect(mapStateToProps, {
  getConfigurations,
  addConfiguration,
  updateConfiguration,
  deleteConfiguration,
  searchConfigurations,
  selectConfiguration,
  clearError,
  getCategories,
})(AIAssists);
